import { createFileRoute, Link } from "@tanstack/react-router";
import LegalPage from "@/components/LegalPage";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms & Conditions — Backland" },
      {
        name: "description",
        content:
          "Backland's terms and conditions: check-in, cancellation, occupancy, waiver of liability, and other camp policies.",
      },
      { property: "og:title", content: "Terms & Conditions — Backland" },
      {
        property: "og:description",
        content: "Camp policies, cancellation terms, and the guest waiver.",
      },
      { property: "og:url", content: "https://www.travelbackland.com/terms" },
    ],
    links: [{ rel: "canonical", href: "https://www.travelbackland.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalPage eyebrow="Legal" title="Terms, Conditions & Data Privacy">
      <h2>Check-In</h2>
      <p>Check-in is after 3 p.m. on the day of arrival.</p>

      <h2>Check-Out</h2>
      <p>Check-out is before 11 a.m. on the day of departure.</p>

      <h2>Pets</h2>
      <p>
        Some pets are welcome for an additional pet fee. See our full{" "}
        <Link to="/pet-policy" className="underline hover:text-gold">
          pet policy here
        </Link>
        .
      </p>

      <h2>Guarantee Policy</h2>
      <p>
        A deposit equal to 100% of your total stay is required no later than 24 hours after
        your reservation is confirmed and will be charged to your credit card. If credit
        card payment is invalid, your reservation will be cancelled.
      </p>

      <h2>Cancellation and Deposit Policy</h2>
      <p>
        A deposit equal to the full price of your reservation is due at the time of booking.
        Guests may cancel 14 days before arrival to receive a full refund of their deposit.
        Cancellations made less than 14 days but more than 3 days prior to arrival are
        eligible for date changes. A change fee of $25 per reservation and rate difference,
        if applicable, will be due at the time of re-booking. Should the new fare be lower
        than that of the original reservation, the difference will not be refunded. Deposits
        for reservations changed less than 14 days before arrival will be automatically
        considered non-refundable regardless of the time until the new reservation date,
        however, additional changes could be made up to 3 days before arrival date. Deposits
        for any reservations cancelled less than 14 days prior to arrival date are
        non-refundable. Early departures or no shows are chargeable at the full amount of
        the deposit.
      </p>

      <h2>Responsible Party</h2>
      <p>
        Guests must be at least 18 years old to reserve a tent and an adult must always
        accompany minor children while on the premises. By signing a reservation agreement
        the signer acknowledges that they are over 18 years old and assumes responsibility
        for all guests included in their reservation. It is the responsibility of the signer
        to ensure all guests included in their reservation are aware of and agree to abide
        by all Camp Policies.
      </p>

      <h2>Quiet Hours</h2>
      <p>
        Quiet hours are from 11 p.m. to 7 a.m. At all times guests are required to act in a
        manner that preserves the quiet enjoyment of the camp for all guests.
      </p>

      <h2>Smoking</h2>
      <p>
        Our tents, lobby, restaurant, and spa area are 100% smoke free. Smoking is allowed
        only in designated areas.
      </p>

      <h2>Maximum Occupancy</h2>
      <p>
        An accurate number of guests must be included in your reservation at the time of
        booking. In no case shall the maximum occupancy of a tent be exceeded. Suite and Sky
        Suite Tents have a maximum occupancy of 4 persons (accommodated in one king bed and
        one sofa bed). Grand Suite and Grand Sky Suite Tents have a maximum occupancy of 6
        persons (accommodated in one king bed, one sofa bed and one twin bunk bed). One
        additional person 12 and under may be added with a cot request to any tent for an
        additional $75 per night. Only 1 cot may be added to a tent. A child 6 or under may
        share a bed with his/her parent(s) at no additional charge. Children 2 and under
        stay free of charge. “Pack and Play” or similar collapsible infant beds are provided
        on request free of charge, subject to availability.
      </p>

      <h2>After-Hours Assistance / Security</h2>
      <p>
        In an emergency dial 911. For all other after-hours assistance needs please call{" "}
        <a href="tel:+19282885441" className="underline hover:text-gold">
          928-288-5441
        </a>
        .
      </p>

      <h2>Vehicles and Parking</h2>
      <p>
        The road to Backland is part of the adventure, but it may not be for everyone. The
        last 8 miles (around 15–20 mins) of your trip will be on scenic and beautiful
        unpaved Forest Service roads. These roads are graveled, well maintained, clearly
        marked and are suitable for most vehicles. We do not suggest motorcycles, or ultra
        low clearance sports cars. Four-wheel drive is not necessary. You can expect there
        to be some wash boarding on the roads and depending on the weather, potential areas
        with shallow water. After your trip to Backland your car will likely be dusty (or
        muddy if it has rained). There is one designated parking spot per tent located
        adjacent to your tent. Additional parking is available by the lobby.
      </p>

      <h2>Right to Refuse Service</h2>
      <p>
        Backland is privately owned and operated and reserves the right to refuse to admit,
        refuse service or accommodation, or remove a person, without refund, who refuses to
        abide by applicable State and Federal Laws or these Camp Policies.
      </p>

      <h2>Campfires</h2>
      <p>
        Campfires are only allowed in the designated fire pits. If Forest Service fire
        restrictions are in place campfires may be unavailable. All other fires, including
        fireworks, are prohibited.
      </p>

      <h2>Damage and/or Theft of Camp Property</h2>
      <p>
        You are liable for any damage caused by you or any person in your party (whether by
        deliberate, negligent, or reckless act) to the tent(s), camp premises or property.
        Backland reserves the right to retain your credit card and/or debit card details as
        presented during your reservation, and you hereby authorize Backland to charge or
        debit the credit/debit card in such amounts as it shall in its sole discretion deem
        necessary and reasonable to compensate or make good the cost, expenses or lost
        revenue incurred or suffered by Backland as a result of the aforesaid damage. If
        credit/debit card charges fail to process, we reserve the right to collect payment
        by other means.
      </p>

      <h2>Waiver</h2>
      <p>
        In making a reservation with Backland, you acknowledge that you have read the
        following Guest Waiver and are aware that you are required to sign it upon or prior
        to check in.
      </p>

      <h2>Assumption of Risk, Waiver of Liability and Indemnity Agreement</h2>
      <p>
        Moose Ranch LLC, operating under the Trade Name of Backland (“Backland”) strives to
        take reasonable measures to ensure the safety of its guests. In consideration for
        Backland’s services, I, on behalf of myself, and my minor child (if applicable), and
        my heirs and/or representatives, voluntarily enter into this ASSUMPTION OF RISK,
        WAIVER OF LIABILITY AND INDEMNITY AGREEMENT (“Agreement”). I hereby covenant not to
        sue and voluntarily agree to waive, release, indemnify, and hold harmless Backland
        and its officers, directors, agents, employees, owners, affiliates, volunteers,
        contractors, representatives, and property partners (collectively, “Releasees”) from
        any and all liability, causes of action, or claims for any injuries (including
        serious injuries and death) or damages as a result of my participation in any
        Backland Activity (which specifically includes activities put on by Backland and/or
        any activity that is run through a third party, including Backland’s property
        partners), whether caused by the ordinary negligence of Backland or the Releasees. I
        understand that this Agreement does not waive any claims resulting from gross
        negligence, willful, reckless or wanton misconduct by Backland. I also understand
        that Backland is not responsible for the acts of third parties under this Agreement.
      </p>
      <p>
        I voluntarily assume full responsibility for any risks of loss, property damage, or
        personal injury, including serious injuries or death, that may be sustained by me
        (or my minor child), as a result of my (or my minor child’s) stay at Backland or
        participation in any Backland Activity, whether caused by negligence of Backland or
        otherwise. I have voluntarily elected to enter Backland’s premises and engage in
        Backland’s activities, including, but in no way limited to bicycling, yoga,
        horseback riding and lawn games, knowing that conditions may be hazardous, or may
        become hazardous to me and/or my property, and that access to medical personnel
        and/or facilities may be limited. Certain inherent risks involved in outdoor
        activities offered by Backland, include but are not limited to: inclement
        weather-related hazards, including extreme temperatures, wind, fire, ice, tornadoes;
        rugged terrain or bodies of water; wild animals and livestock; and insects and
        poisonous plants. I also understand and acknowledge that additional unknown or
        unanticipated risks do exist that may result in injury or death. I understand that
        these risks are increased by the use of drugs or alcohol.
      </p>
      <p>
        I understand that it is my responsibility to consult with my physician prior to
        participating in any Backland Activity. In the event that I become incapacitated
        while participating in a Backland Activity, I hereby authorize Backland, and
        emergency medical personnel to make emergency medical decisions on my behalf
        (including but not limited to CPR and/or an AED). I also authorize Backland to
        arrange emergency and non-emergency transportation, including transportation on any
        Backland owned vehicle, including but not limited to boats and other watercraft. I
        also agree to indemnify and hold harmless Backland from any and all costs associated
        with my emergency care and transportation.
      </p>
      <p>
        My participation in any Backland Activity signifies that I have found the Equipment
        (which includes any equipment available or accessible on the property or any
        personal equipment I bring with me) and areas to be safe and acceptable to me. I
        accept full and sole responsibility for the condition and adequacy of my personal
        Equipment.
      </p>
      <p>
        I assume all risk of driving to Backland and Backland is not liable for any damage
        to vehicles owned or rented by me.
      </p>
      <p>
        This Agreement shall be governed by the laws of the State of Arizona. I agree to
        submit any and all claims arising from my stay and/or participation in Backland
        Activities to binding Arbitration in Coconino County. I acknowledge that I am giving
        up the right to have my dispute decided in a court of law by a judge or jury. I
        further understand and agree that Backland will be entitled to recover attorneys’
        fees and costs incurred defending against an action barred by this Agreement.
      </p>
      <p>
        We reserve the right to charge guests the cost of rectifying damage, caused by the
        deliberate, negligent or reckless act of the guest to the hotel’s property or
        structure. The amount charged to the guest will be the following:
      </p>
      <ul>
        <li>
          $250 for minor to moderate damage, as determined by Backland Retreats in its sole
          discretion.
        </li>
        <li>
          $500 for major damage, as determined by Backland Retreats in its sole discretion,
          plus additional costs/losses, if any, incurred by Backland Retreats in the course
          of repairing the damage including, but in no way limited to the actual cost of
          materials or outside contractors, and the revenue lost should it be necessary to
          take a tent or other facility off line to effectuate the repair.
        </li>
      </ul>
      <p>
        Should this damage come to light after the guest has departed, we reserve the right
        to make a charge to the guest’s credit / debit card, or send an invoice for the
        amount to the registered address.
      </p>
      <p>
        This Agreement is the entire agreement between the parties, and shall supersede any
        and all prior agreements. If any provision of this Agreement is found to be illegal
        or invalid, the remaining provisions shall remain enforceable to the fullest extent
        permitted by law.
      </p>
      <p className="uppercase tracking-wide text-sm">
        By signing the guest waiver at check-in, you certify that you have carefully
        reviewed this Agreement and voluntarily agree to its terms. You also certify that
        you have received a copy of Backland’s Policies, Terms and Conditions, and
        understand that failure to comply with these policies may be grounds for immediate
        removal from the property.
      </p>
    </LegalPage>
  );
}
